import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Card,
    Container,
    Grid,
    Stack,
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";

import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DefaultNavbar from "../../components/TopNavBar/DefaultNavBar";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Upload as UploadIcon, Download as DownloadIcon } from "@mui/icons-material";
import { useCimbeo } from "../../context";

const UserSelectIcon = () => (
    <AddIcon sx={{
        width: 24,
        height: 24,
        color: '#2B5587',
        position: 'absolute',
        right: 12,
        top: 'calc(50% - 12px)',
        pointerEvents: 'none'
    }} />
);

function ProjectSettings() {
    const navigate = useNavigate();
    const { projectId, projects, setProjects, ingestedDocuments, allUsers, setAllUsers, setIngestedDocuments } = useCimbeo();
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editForm, setEditForm] = useState({
        name: "",
        description: "",
        access: [],
    });

    // Get current project from projectId
    const currentProject = projects.find(p => p.id == projectId);

    // Get users who have access to the current project
    const getProjectUsers = () => {
        if (!currentProject) return [];
        return allUsers.filter(user => 
            (user.projectIds || []).includes(projectId)
        );
    };

    // Initialize edit form when current project changes
    useEffect(() => {
        if (currentProject) {
            const usersWithAccess = allUsers
                .filter(user => (user.projectIds || []).includes(projectId))
                .map(user => user.id);
            
            setEditForm({
                name: currentProject.title,
                description: currentProject.description,
                access: usersWithAccess,
            });
        }
    }, [currentProject, allUsers, projectId]);

    const handleEditOpen = () => {
        setEditDialogOpen(true);
    };

    const handleEditClose = () => {
        setEditDialogOpen(false);
    };

    const handleFormChange = (field) => (event) => {
        setEditForm(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleSave = () => {
        if (currentProject) {
            // Update the project in the projects list
            const updatedProjects = projects.map(p => 
                p.id === projectId 
                    ? {
                        ...p,
                        title: editForm.name,
                        description: editForm.description,
                        documents: p.documents || []
                    }
                    : p
            );
            setProjects(updatedProjects);

            // Update users' projectIds
            const updatedUsers = allUsers.map(user => {
                const hasAccess = editForm.access.includes(user.id);
                const currentProjectIds = user.projectIds || [];
                
                if (hasAccess && !currentProjectIds.includes(projectId)) {
                    // Add project to user's access
                    return {
                        ...user,
                        projectIds: [...currentProjectIds, projectId]
                    };
                } else if (!hasAccess && currentProjectIds.includes(projectId)) {
                    // Remove project from user's access
                    return {
                        ...user,
                        projectIds: currentProjectIds.filter(id => id !== projectId)
                    };
                }
                return user;
            });
            
            setAllUsers(updatedUsers);
            handleEditClose();
        }
    };

    const handleDocumentUpload = () => {
        if (currentProject) {
            navigate('/artifact-ingestion', { 
                state: { 
                    selectedProject: {
                        id: projectId,
                        title: currentProject.title
                    }
                }
            });
        }
    };

    const getProjectDocuments = () => {
        if (!currentProject) return [];
        return ingestedDocuments.filter(doc => doc.projectName === currentProject.title);
    };

    const handleDocumentDelete = (documentId) => {
        if (currentProject) {
            setIngestedDocuments(prev => prev.filter(doc => doc.id !== documentId));
        }
    };

    const handleDocumentDownload = (documentId) => {
        // Implement document download logic
        console.log("Downloading document:", documentId);
    };

    if (!currentProject) {
        return (
            <DashboardLayout>
                <DefaultNavbar />
                <MDBox py={3}>
                    <Container maxWidth="xl">
                        <Card>
                            <MDBox p={3} textAlign="center">
                                <MDTypography variant="body1">
                                    Please select a project from the dropdown menu in the navigation bar to view and edit settings.
                                </MDTypography>
                            </MDBox>
                        </Card>
                    </Container>
                </MDBox>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DefaultNavbar />
            <MDBox py={3}>
                <Container maxWidth="xl">
                    <MDBox mb={3}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12}>
                                <MDTypography variant="h5">Project Settings</MDTypography>
                            </Grid>
                        </Grid>
                    </MDBox>

                    <Card sx={{ overflow: 'visible', backgroundColor: "navy.main" }}>
                        <MDBox p={3}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <MDTypography variant="h6">{currentProject.title}</MDTypography>
                                <Button
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={handleEditOpen}
                                    sx={{
                                        borderColor: 'gold.main',
                                        color: 'gold.main',
                                        '&:hover': {
                                            borderColor: 'gold.main',
                                            bgcolor: 'rgba(43, 85, 135, 0.04)',
                                        },
                                    }}
                                >
                                    Edit Settings
                                </Button>
                            </Box>
                            <MDTypography variant="body2" mb={3}>
                                {currentProject.description}
                            </MDTypography>
                            <Box mb={3}>
                                <MDTypography variant="subtitle2" mb={1}>Access:</MDTypography>
                                <Stack direction="row" spacing={1}>
                                    {getProjectUsers().map((user) => (
                                        <Chip
                                            key={user.id}
                                            label={user.realName || user.username}
                                            size="small"
                                            sx={{
                                                bgcolor: 'olive_green.main',
                                                color: 'text.main',
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                            <Divider sx={{ my: 3 }} />
                            <Box>
                                <MDTypography variant="h6" mb={2}>Document Management</MDTypography>
                                <Button
                                    variant="contained"
                                    startIcon={<UploadIcon />}
                                    onClick={handleDocumentUpload}
                                    sx={{
                                        mb: 3,
                                    }}
                                >
                                    Ingest Artifacts
                                </Button>
                                
                                {(getProjectDocuments().length > 0) ? (
                                    <Box>
                                        <MDTypography variant="subtitle2" mb={2}>Project Documents</MDTypography>
                                        <Stack spacing={2}>
                                            {getProjectDocuments().map((doc) => (
                                                <Box
                                                    key={doc.id}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        p: 2,
                                                        bgcolor: 'slate_gray.main',
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Box>
                                                        <MDTypography variant="body2" color="black">{doc.name}</MDTypography>
                                                        <MDTypography variant="caption" color="black">
                                                            Uploaded by: {doc.uploadedBy} • Type: {doc.type} • Status: {doc.status} • {new Date(doc.timestamp).toLocaleDateString()}
                                                        </MDTypography>
                                                    </Box>
                                                    <Stack direction="row" spacing={1}>
                                                        <Tooltip title="Download Document" arrow placement="top">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDocumentDownload(doc.id)}
                                                                sx={{
                                                                    color: 'dark_teal.main',
                                                                    '&:hover': {
                                                                        bgcolor: 'rgba(43, 85, 135, 0.04)',
                                                                    },
                                                                }}
                                                            >
                                                                <DownloadIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete Document" arrow placement="top">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDocumentDelete(doc.id)}
                                                                sx={{
                                                                    color: '#dc3545',
                                                                    '&:hover': {
                                                                        bgcolor: 'rgba(220, 53, 69, 0.04)',
                                                                    },
                                                                }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No documents uploaded yet.
                                    </Typography>
                                )}
                            </Box>
                        </MDBox>
                    </Card>
                </Container>
            </MDBox>

            {/* Edit Project Dialog */}
            <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Project Settings</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Project Name"
                            value={editForm.name}
                            onChange={handleFormChange("name")}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: "56px",
                                },
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={editForm.description}
                            onChange={handleFormChange("description")}
                            multiline
                            rows={4}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="project-users-label">Project Users</InputLabel>
                            <Select
                                labelId="project-users-label"
                                label="Project Users"
                                fullWidth
                                multiple
                                value={editForm.access}
                                onChange={handleFormChange("access")}
                                IconComponent={() => <UserSelectIcon />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((userId) => {
                                            const user = allUsers.find(u => u.id === userId);
                                            return user ? (
                                                <Chip
                                                    key={userId}
                                                    label={user.realName || user.username}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#2B5587',
                                                        color: '#FFFFFF',
                                                    }}
                                                />
                                            ) : null;
                                        })}
                                    </Box>
                                )}
                                sx={{
                                    height: "56px",
                                    '& .MuiSelect-select': {
                                        padding: '16.5px',
                                        fontSize: '1rem',
                                    },
                                }}
                            >
                                {allUsers.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.realName || user.username}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditClose}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ bgcolor: "#2B5587", color: "#FFFFFF" }}>
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}

export default ProjectSettings; 